import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import ThumborJS from "thumborjs";

const BannerCarousel = dynamic(() => import("./Banner"), {
  ssr: false,
  loading: () => null,
});

const imageUrl = process.env.NEXT_PUBLIC_IMAGE_URL;
const thumborKey = process.env.NEXT_PUBLIC_THUMBOR_KEY;
const thumborUrl = process.env.NEXT_PUBLIC_THUMBOR_URL;

const getImageUrl = (path, width, height) => {
  const ratio = width / height;
  return new ThumborJS(thumborUrl, thumborKey)
    .imgpath(path)
    .resize(width, Math.ceil(width / ratio))
    .filter("format(webp)")
    .genurl();
};

const HeroBanner = ({ bannerData, queryParam }) => {
  const router = useRouter();
  const [carouselInitialSlide, setCarouselInitialSlide] = useState(null);
  const firstBanner = bannerData?.[0];
  const nextDesktopPath = bannerData?.[1]?.website_image_path;
  const nextMobilePath = bannerData?.[1]?.mobile_image_path;

  useEffect(() => {
    BannerCarousel.preload?.();

    // เตรียมรูปสไลด์ถัดไปไว้ก่อนถึงเวลาเปลี่ยน เพื่อไม่ให้เห็นพื้นขาวระหว่าง slide
    if (nextDesktopPath || nextMobilePath) {
      const useMobileImage = window.matchMedia("(max-width: 639px)").matches;
      const nextPath = (useMobileImage
        ? nextMobilePath
        : nextDesktopPath
      )?.replace(/\/uploads/g, "");

      if (nextPath) {
        const preloadImage = new window.Image();
        preloadImage.src = getImageUrl(
          `${imageUrl}${nextPath}`,
          useMobileImage ? 480 : 1024,
          useMobileImage ? 359 : 324,
        );
      }
    }

    const timeoutId = window.setTimeout(
      () => setCarouselInitialSlide(0),
      20000,
    );
    return () => window.clearTimeout(timeoutId);
  }, [bannerData?.length, nextDesktopPath, nextMobilePath]);

  if (!firstBanner) return null;

  if (carouselInitialSlide !== null) {
    const desktopPath = firstBanner?.website_image_path?.replace(
      /\/uploads/g,
      "",
    );
    const mobilePath = firstBanner?.mobile_image_path?.replace(/\/uploads/g, "");
    const fallbackImage = `${process.env.NEXT_PUBLIC_FRONTEND_URL}/static/images/thumbnail.jpg`;
    const desktopImage = getImageUrl(
      desktopPath ? `${imageUrl}${desktopPath}` : fallbackImage,
      1024,
      324,
    );
    const mobileImage = getImageUrl(
      mobilePath ? `${imageUrl}${mobilePath}` : fallbackImage,
      480,
      359,
    );

    return (
      <div className="relative w-full overflow-hidden aspect-[750/560] sm:aspect-[2592/820]">
        <picture>
          <source media="(max-width: 639px)" srcSet={mobileImage} />
          <img
            src={desktopImage}
            alt=""
            width={1024}
            height={324}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </picture>
        <div className="absolute inset-0">
          <BannerCarousel
            bannerData={bannerData}
            indexValue="website_image_path"
            mobileIndexValue="mobile_image_path"
            bannerWidth={1024}
            bannerHeight={324}
            queryParam={queryParam}
            initialSlide={carouselInitialSlide}
            slideOnMount={bannerData?.length > 1}
          />
        </div>
      </div>
    );
  }

  const desktopPath = firstBanner?.website_image_path?.replace(
    /\/uploads/g,
    "",
  );
  const mobilePath = firstBanner?.mobile_image_path?.replace(/\/uploads/g, "");
  const fallbackImage = `${process.env.NEXT_PUBLIC_FRONTEND_URL}/static/images/thumbnail.jpg`;
  const desktopImage = getImageUrl(
    desktopPath ? `${imageUrl}${desktopPath}` : fallbackImage,
    1024,
    324,
  );
  const mobileImage = getImageUrl(
    mobilePath ? `${imageUrl}${mobilePath}` : fallbackImage,
    480,
    359,
  );
  const url =
    firstBanner?.event_type === "live" ? "/live" : firstBanner?.url || "";

  const openBanner = () => {
    if (url) router.push(`${url}${queryParam ? `?${queryParam}` : ""}`);
  };

  return (
    <div
      className="relative w-full aspect-[750/560] sm:aspect-[2592/820]"
      onClick={openBanner}
      onMouseEnter={() => setCarouselInitialSlide(0)}
      onTouchMove={() => setCarouselInitialSlide(0)}
    >
      <picture>
        <source media="(max-width: 639px)" srcSet={mobileImage} />
        <img
          src={desktopImage}
          alt={firstBanner?.title || "Luxury29 promotion"}
          width={1024}
          height={324}
          loading="eager"
          fetchpriority="high"
          decoding="sync"
          className={`absolute inset-0 h-full w-full object-cover ${
            url ? "banner-img-pointer" : "banner-img"
          }`}
        />
      </picture>
    </div>
  );
};

export default HeroBanner;
